import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { catchError, forkJoin, of, Subject, Subscription, takeUntil } from 'rxjs';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { BookAdjustmentTabService } from './book-adjustment-tab.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-trial-balance-book-adjustment-tab',
    templateUrl: './book-adjustment-tab.component.html',
    styleUrls: ['./book-adjustment-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BookAdjustmentTabComponent implements OnInit, OnDestroy {
  private isInitialized = false;
  private unsubscribe$ = new Subject<void>();
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  title = 'Book Adjustment Reclass';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  existingLoading = false;
  noRecords = false;
  viewOnly = false;
  showReclassForm = true;
  showReclassType = true;
  showNovYrReclassType = true;
  loadingSysAcct = true;
  loadingTpAcct = true;
  isMaxLimitReached = false;

  RECLASS_DESCRIPTION = '';
  RECLASS_TYPE = 'RCY';
  TRANS_HEADER_KEY: string | number = '';
  reclassTotal = 0;
  accounts: any[] = [];
  reclassArray: any[] = [];
  delTransDetails: any[] = [];
  localAccDropdownData: any[] = [];
  limitedAccDropdownData: any[] = [];
  tpDropdownData: any[] = [];
  limitedTpDropdownData: any[] = [];
  tpSearchResults: any[] = [];
  loadingTpSearch = false;
  activeTPSearchIndex = -1;
  reclassDescDropdownData: any[] = [];
  commentsDescDropdownData: any[] = [];
  reclassDescFullData: any[] = [];
  commentsDescFullData: any[] = [];
  lazyLoadObj: any = {};
  modal = { R_STD_DESC: '', R_CMT: '' };
  codeCombination: any = null;
  taxYearBegin = '';
  taxYearEnd = '';
  mainScreenKey = 0;
  gridType = '';
  sourceAcctType: number | null = null;

  baseURLs: any = {};
  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  filterParams: any = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private service: BookAdjustmentTabService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.setupInitialData();
      this.loadAllData();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupInitialData(): void {
    this.gridType = this.rowData.GRID_TYPE || '';
    this.sourceAcctType = this.rowData.SYSTEM_ACCT_TYPE;
    // Hardcoded to 18 to match legacy AngularJS (trialBalanceController.js:83).
    // Backend SQL for action `unmge8` (Account dropdown) filters by screen_key
    // and returns no rows when called with the parent grid's screen_key (e.g. 175).
    this.mainScreenKey = 18;

    if (this.rowData.TAX_YEAR_BEGIN) {
      const d = new Date(this.rowData.TAX_YEAR_BEGIN);
      this.taxYearBegin = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    }
    if (this.rowData.TAX_YEAR_END) {
      const d = new Date(this.rowData.TAX_YEAR_END);
      this.taxYearEnd = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    }

    this.showReclassType = this.rowData.SOURCE_SYSTEM !== 'DCS';
    this.showNovYrReclassType = this.rowData.NOV_YEAR_END !== 'N';

    if (this.rowData.TRANS_TYPE_ACTUAL) {
      switch (this.rowData.TRANS_TYPE_ACTUAL) {
        case 'LOCAL_BOOK_ADJ':
          this.RECLASS_TYPE = 'RCY';
          break;
        case 'LOCAL_BOOK_ADJ_CY':
          this.RECLASS_TYPE = 'CYD';
          break;
        case 'LOCAL_BOOK_ADJ_PY':
          this.RECLASS_TYPE = 'PYD';
          break;
        default:
          this.RECLASS_TYPE = 'RCY';
      }
    }

    if (this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') {
      this.buildAccountRecord(this.rowData, 0);
    } else {
      this.accounts.push(this.createBlankAccount());
    }
  }

  private loadAllData(): void {
    const balSheetAccts = [1, 2, 3];
    const acctType = this.sourceAcctType || 0;
    const acctTypeStr = balSheetAccts.includes(acctType) ? '1,2,3' : '5,6,7';
    const isSystemAcctSearch = this.gridType === 'SYSTEM_BOOK_TO_TAX_SUMMARY' ? 'Y' : 'N';
    const globalParams = this.globalService?.globalParams || this.globalService || {};
    const taxYear = this.filterParams.tax_year || globalParams.tax_year || this.rowData.TAX_YEAR;
    const scenario = this.filterParams.scenario || globalParams.scenario || this.rowData.SCENARIO;
    const isDCS = this.rowData.SOURCE_SYSTEM === 'DCS';

    const accounts$ = this.service.getAccounts(
      this.GENERAL_CONFIG.base_url,
      {
        screen_key: this.mainScreenKey,
        screen_type: 'grid',
        activity_key: this.filterParams.activity_key || globalParams.activity_key || this.rowData.ACTIVITY_KEY || '',
        tax_year: taxYear,
        scenario: scenario,
        jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key || globalParams.jcd_key || '',
        jcd_ta_key: this.filterParams.jcd_ta_key || globalParams.jcd_ta_key || this.rowData.JCD_TA_KEY || '',
        filing_key: this.filterParams.filing_key || globalParams.filing_key || this.rowData.CONSOL_GROUP_KEY || 1,
        filter_by: this.filterParams.filter_by || globalParams.filter_by || 9,
        issue_id: this.filterParams.issue_id || globalParams.issue_id || '',
        issue_id_for_tx_save: this.filterParams.issue_id_for_tx_save || globalParams.issue_id_for_tx_save || '',
        issue_short_desc: this.filterParams.issue_short_desc || globalParams.issue_short_desc || '',
        is_locked: this.filterParams.is_locked || globalParams.is_locked || 'N',
        combination_key: this.rowData.COMBINATION_KEY,
        ge_gecs_entity: this.rowData.GE_GECS_ENTITY || this.rowData.SOURCE_LOB || '',
        chart_key: this.rowData.LOCAL_CHART_KEY,
        parent_me_string: this.filterParams.parent_me_string || globalParams.parent_me_string || '',
        parent_system_acct_key: this.rowData.SYSTEM_ACCT_KEY || 0,
        source_type: isDCS ? 'D' : 'F',
        search_type: 'ACCT',
        search_string: '',
        acct_type_str: acctTypeStr,
        system_acct_search: isSystemAcctSearch
      },
      this.globalService
    ).pipe(catchError(() => of([])));

    // Legacy uses getTradingPartnersFir (50swfi) for both DCS and FIR.
    // The DCS-specific path (sckfcq) was deprecated/commented out in legacy.
    const tp$ = this.service.getTradingPartnersFir(this.GENERAL_CONFIG.base_url, taxYear, scenario, '', this.globalService).pipe(catchError(() => of([])));
    const existing$ = this.service.loadExistingReclass(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      this.rowData.COMBINATION_KEY,
      this.rowData.LOCAL_CHART_KEY,
      this.rowData.SYSTEM_ACCT_KEY || 0,
      this.globalService
    ).pipe(catchError(() => of([])));

    const jcdKey = this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.globalParams?.jcd_key;
    const stdLookupParams = {
      tax_year: taxYear,
      scenario: scenario,
      screen_key: this.rowData.SCREEN_KEY || this.mainScreenKey || 175,
      screen_type: 'grid',
      activity_key: this.filterParams.activity_key || globalParams.activity_key || '',
      filing_key: this.filterParams.filing_key || globalParams.filing_key || this.rowData.CONSOL_GROUP_KEY || 1,
      filter_by: this.filterParams.filter_by || globalParams.filter_by || 9,
      is_locked: this.filterParams.is_locked || globalParams.is_locked || 'N',
      issue_id: this.filterParams.issue_id || globalParams.issue_id || '',
      issue_id_for_tx_save: this.filterParams.issue_id_for_tx_save || globalParams.issue_id_for_tx_save || '',
      issue_short_desc: this.filterParams.issue_short_desc || globalParams.issue_short_desc || '',
      jcd_key: jcdKey || '',
      jcd_ta_key: this.filterParams.jcd_ta_key || globalParams.jcd_ta_key || '',
      parent_me_string: this.filterParams.parent_me_string || globalParams.parent_me_string || ''
    };
    const stdDesc$ = isDCS
      ? this.service.getStdReclassDescriptions(this.GENERAL_CONFIG.base_url, stdLookupParams, this.globalService).pipe(catchError(() => of([])))
      : of([]);
    const stdCmt$ = isDCS
      ? this.service.getStdReclassComments(this.GENERAL_CONFIG.base_url, stdLookupParams, this.globalService).pipe(catchError(() => of([])))
      : of([]);

    const hasTransType = this.rowData.hasOwnProperty('TRANS_TYPE_ACTUAL');
    let dtlsAdj$: any = of([]);
    if (hasTransType) {
      let adjSource = 'M';
      switch (this.rowData.TRANS_TYPE_ACTUAL) {
        case 'LOCAL_BOOK_ADJ':
          adjSource = 'M';
          break;
        case 'LOCAL_BOOK_ADJ_PY':
          adjSource = 'P';
          break;
        default:
          adjSource = 'C';
          break;
      }
      dtlsAdj$ = this.service.getAmountsForDtlsAdj(
        this.GENERAL_CONFIG.base_url,
        this.rowData.COMBINATION_KEY,
        this.rowData.LOCAL_ACCT_KEY,
        this.rowData.LOCAL_CHART_KEY,
        this.rowData.TP_KEY || 0,
        adjSource,
        this.globalService
      ).pipe(catchError(() => of([])));
    }

    const codeCombo$ = !isDCS && this.rowData.COMBINATION_KEY
      ? this.service.getCodeCombination(this.GENERAL_CONFIG.base_url, this.rowData.COMBINATION_KEY).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin([accounts$, tp$, existing$, stdDesc$, stdCmt$, dtlsAdj$, codeCombo$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([accountsData, tpData, existingData, stdDescData, stdCmtData, dtlsAdjData, codeComboData]: any[]) => {
        this.codeCombination = codeComboData || null;
        this.localAccDropdownData = accountsData || [];
        this.limitedAccDropdownData = this.localAccDropdownData;
        this.lazyLoadObj.totalDrpList = this.localAccDropdownData;
        this.lazyLoadObj.cachedDrpList = this.localAccDropdownData.slice(0, 10);
        this.loadingSysAcct = false;

        this.tpDropdownData = tpData || [];
        this.limitedTpDropdownData = this.tpDropdownData;
        this.loadingTpAcct = false;

        this.reclassArray = this.formatReclassData(existingData || []);
        this.setExistingReclassTotal(this.reclassArray);
        this.existingLoading = false;
        this.noRecords = this.reclassArray.length === 0;

        if (stdDescData && stdDescData.length) {
          this.reclassDescFullData = stdDescData;
          this.reclassDescDropdownData = [...new Set(stdDescData.map((item: any) => item.RECLASSSTDDESC))];
        }
        if (stdCmtData && stdCmtData.length) {
          this.commentsDescFullData = stdCmtData;
          this.commentsDescDropdownData = [...new Set(stdCmtData.map((item: any) => item.RECLASSSTDCOMMENTS))];
        }

        if (dtlsAdjData && dtlsAdjData.length && this.accounts.length > 0) {
          const sourceAcct = this.accounts.find(a => a.TYPE === 'source');
          if (sourceAcct) {
            sourceAcct.BOOK_AMT = dtlsAdjData[0].LOCAL_BOOK_AMT;
            sourceAcct.PREV_ADJ_AMT = dtlsAdjData[0].LOCAL_BOOK_ADJ;
          }
        }

        this.showFormLoader = false;
        this.cdr.detectChanges();
      });
  }

  private createBlankAccount(): any {
    return {
      ACCTTP: '',
      LOCAL_ACCT_KEY: 0,
      SYSTEM_ACCT_KEY: 0,
      SYSTEM_ACCT: '',
      SYSTEM_ACCT_DESC: '',
      LOCAL_ACCT: '',
      LOCAL_ACCT_DESC: '',
      TP_ENTITY: '',
      TP_KEY: 0,
      TP_REP_PD: 0,
      IS_TP_EXISTS: false,
      BOOK_AMT: null,
      PREV_ADJ_AMT: null,
      CUR_ADJ_AMT: null,
      TYPE: 'blank',
      IS_DELETE: 'NO',
      TRANS_DETAILS_KEY: 0,
      ISBOOKAMTLOADING: false,
      ISBOOKADJLOADING: false,
      INVALIDACCTTP: false
    };
  }

  addTargetAccount(): void {
    this.accounts.push(this.createBlankAccount());
    this.cdr.detectChanges();
  }

  removeAccount(index: number): void {
    const account = this.accounts[index];
    if (account.TYPE === 'edit') {
      account.IS_DELETE = 'YES';
      this.delTransDetails.push(account);
    }
    this.accounts.splice(index, 1);
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  private formatTpEntity(tp: any): string {
    const tpKey = Number(tp?.TP_KEY) || 0;
    if (!tpKey || !tp?.TP_LEID || !tp?.TP_CDR_NO) return '';
    return `${tp.TP_LEID}-${tp.TP_CDR_NO}(Pd:${tp.TP_REP_PD ?? 0})`;
  }

  private formatReclassData(data: any[]): any[] {
    const reclasses: any[] = [];
    const accounts: any[] = [];
    data.forEach(item => {
      if (item.ROW_INDICATOR === 1) {
        reclasses.push({ ...item, ACCOUNTS: [] });
      } else {
        const rebuilt = this.formatTpEntity(item);
        accounts.push({
          ...item,
          TP_ENTITY: rebuilt || item.TP_ENTITY || '',
          TYPE: 'edit'
        });
      }
    });
    reclasses.forEach(r => {
      r.ACCOUNTS = accounts.filter(a => a.TRANS_HEADER_KEY === r.TRANS_HEADER_KEY);
    });
    return reclasses;
  }

  private setExistingReclassTotal(array: any[]): void {
    array.forEach(r => {
      if (r.ACCOUNTS) {
        r.ACCOUNTS.forEach((a: any) => {
          a.TOTAL = (parseFloat(a.BOOK_AMT) || 0) + (parseFloat(a.PREV_ADJ_AMT) || 0) + (parseFloat(a.CUR_ADJ_AMT) || 0);
        });
      }
    });
  }

  private buildAccountRecord(data: any, index: number): void {
    const tpEntity = this.formatTpEntity(data);
    const tpExists = tpEntity !== '';
    const tpRepPd = tpExists ? data.TP_REP_PD : 0;
    let totBookAdj = data.BOOK_ADJ || 0;
    if (index < 1 && this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') {
      totBookAdj = (data.BOOK_ADJ || 0) + (data.PY_DECEMBER || 0) + (data.CY_DECEMBER || 0);
    }

    const record: any = {
      ACCTTP: `${data.LOCAL_ACCT_KEY}${data.TP_KEY}${tpRepPd}`,
      LOCAL_ACCT_KEY: data.LOCAL_ACCT_KEY,
      SYSTEM_ACCT_KEY: data.SYSTEM_ACCT_KEY,
      SYSTEM_ACCT: data.SYSTEM_ACCT,
      SYSTEM_ACCT_DESC: data.SYSTEM_ACCT_DESC,
      LOCAL_ACCT: data.LOCAL_ACCT,
      LOCAL_ACCT_DESC: data.LOCAL_ACCT_DESC,
      TP_ENTITY: tpEntity,
      TP_KEY: data.TP_KEY,
      TP_REP_PD: tpRepPd,
      IS_TP_EXISTS: tpExists,
      BOOK_AMT: data.BOOK_AMT,
      PREV_ADJ_AMT: totBookAdj,
      CUR_ADJ_AMT: 0,
      TYPE: index < 1 ? 'source' : 'target',
      TRANS_DETAILS_KEY: 0,
      IS_DELETE: 'NO',
      ISBOOKAMTLOADING: false,
      ISBOOKADJLOADING: false,
      INVALIDACCTTP: false
    };

    if (data.TP_KEY !== 0) {
      record.tpObject = { TP_ENTITY: tpEntity, TP_KEY: data.TP_KEY, TP_REP_PD: tpRepPd, TP_LEID: data.TP_LEID, TP_CDR_NO: data.TP_CDR_NO };
      record.tpSearchText = tpEntity;
    }

    if (this.accounts[index] === undefined) {
      record.TYPE = 'source';
      this.accounts.push(record);
      this.accounts.push(this.createBlankAccount());
    } else {
      record.TYPE = 'target';
      if (index === 0) {
        if (this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') record.TYPE = 'source';
        this.accounts.push(this.createBlankAccount());
      }
      this.accounts[index] = record;
    }
  }

  setAccount(account: any, index: number): void {
    if (!account) return;
    account.COMBINATION_KEY = this.rowData.COMBINATION_KEY;
    this.buildAccountRecord(account, index);
    this.cdr.detectChanges();
  }

  setTradingPartner(tp: any, index: number): void {
    if (!tp) return;
    const currAcct = this.accounts[index];
    currAcct.TP_ENTITY = this.formatTpEntity(tp);
    currAcct.TP_KEY = tp.TP_KEY;
    currAcct.TP_REP_PD = tp.TP_REP_PD;
    currAcct.ACCTTP = `${currAcct.LOCAL_ACCT_KEY}${tp.TP_KEY}${tp.TP_REP_PD}`;

    if (tp.TP_KEY !== 0) {
      currAcct.ISBOOKAMTLOADING = true;
      currAcct.ISBOOKADJLOADING = true;
      this.cdr.detectChanges();

      this.service.getBookAmtByTp(
        this.GENERAL_CONFIG.base_url,
        this.mainScreenKey,
        this.rowData.COMBINATION_KEY,
        this.rowData.LOCAL_CHART_KEY,
        this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
        'TP',
        `${currAcct.LOCAL_ACCT_KEY}${currAcct.TP_KEY}`,
        this.globalService
      ).pipe(takeUntil(this.unsubscribe$)).subscribe(
        (data: any[]) => {
          currAcct.ISBOOKAMTLOADING = false;
          currAcct.ISBOOKADJLOADING = false;
          if (data.length) {
            currAcct.BOOK_AMT = data[0].BOOK_AMT;
            currAcct.PREV_ADJ_AMT = data[0].BOOK_ADJ;
          } else {
            currAcct.BOOK_AMT = 0;
            currAcct.PREV_ADJ_AMT = 0;
          }
          this.cdr.detectChanges();
        },
        () => {
          currAcct.ISBOOKAMTLOADING = false;
          currAcct.ISBOOKADJLOADING = false;
          currAcct.BOOK_AMT = 0;
          currAcct.PREV_ADJ_AMT = 0;
          this.cdr.detectChanges();
        }
      );
    }
  }

  searchTradingPartners(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    this.accounts[index].tpSearchText = val;
    if (!val || val.length < 3) {
      this.tpSearchResults = [];
      this.activeTPSearchIndex = -1;
      this.cdr.detectChanges();
      return;
    }
    this.activeTPSearchIndex = index;
    this.loadingTpSearch = true;
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    // Legacy uses getTradingPartnersFir (50swfi) for both DCS and FIR typeahead.
    // The DCS-specific dropdown (sckfcq) was deprecated/commented out in legacy.
    this.service.getTradingPartnersFir(this.GENERAL_CONFIG.base_url, taxYear, scenario, val, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          this.tpSearchResults = data || [];
          this.loadingTpSearch = false;
          this.cdr.detectChanges();
        },
        () => {
          this.tpSearchResults = [];
          this.loadingTpSearch = false;
          this.cdr.detectChanges();
        }
      );
  }

  selectTradingPartner(tp: any, index: number): void {
    this.setTradingPartner(tp, index);
    this.accounts[index].tpSearchText = this.formatTpEntity(tp);
    this.tpSearchResults = [];
    this.activeTPSearchIndex = -1;
    this.cdr.detectChanges();
  }

  setSourceTotal(index: number): void {
    this.recalcAccountTotal(index);
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  private recalcAccountTotal(index: number): void {
    const account = this.accounts[index];
    account.TOTAL = (parseFloat(String(account.BOOK_AMT)) || 0) + (parseFloat(String(account.PREV_ADJ_AMT)) || 0) + (parseFloat(String(account.CUR_ADJ_AMT)) || 0);
  }

  private recalcReclassTotal(): void {
    let total = 0;
    this.accounts.forEach(a => {
      if (a.CUR_ADJ_AMT !== null && a.CUR_ADJ_AMT !== undefined && !isNaN(Number(a.CUR_ADJ_AMT))) {
        total += parseFloat(String(a.CUR_ADJ_AMT));
      }
    });
    this.reclassTotal = total;
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }

  removeMessage(): void {
    this.isMaxLimitReached = false;
  }

  showDetails(index: number, show: boolean): void {
    this.reclassArray[index].open = show;
    this.cdr.detectChanges();
  }

  loadReclass(index: number): void {
    this.delTransDetails = [];
    const data = this.reclassArray[index];
    this.RECLASS_DESCRIPTION = data.ADJ_DESC;
    if (this.rowData.SOURCE_SYSTEM === 'DCS') {
      this.modal.R_STD_DESC = data.RECLASS_STD_DESC;
      this.modal.R_CMT = data.RECLASS_STD_COMMENTS;
    }
    this.TRANS_HEADER_KEY = data.TRANS_HEADER_KEY;
    this.accounts = data.ACCOUNTS.map((a: any) => ({ ...a, tpSearchText: a.tpSearchText || a.TP_ENTITY || '' }));
    this.RECLASS_TYPE = data.SHORT_ADJ_TYPE;
    this.viewOnly = data.SHORT_ADJ_TYPE === 'A';
    this.accounts.forEach((_: any, key: number) => this.recalcAccountTotal(key));
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  deleteReclass(reclass: any): void {
    this.existingLoading = true;
    this.cdr.detectChanges();
    const returnObj = {
      trans_header_key: reclass.TRANS_HEADER_KEY,
      type: 'D',
      combination_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.LOCAL_CHART_KEY
    };
    const deleteObj = reclass.ACCOUNTS.map((a: any) => ({
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      ACCT_KEY: a.LOCAL_ACCT_KEY,
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      TP_KEY: a.TP_KEY
    }));
    this.service.deleteReclass(this.GENERAL_CONFIG.base_url, returnObj, deleteObj, this.globalService, this.filterParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: any) => {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
            this.existingLoading = false;
          } else {
            this.gtwSnackbarService.openSnackBar('Reclass has been successfully deleted', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.gtwSnackbarService.openSnackBar('Error deleting reclass', 'gtw-snackbar--red');
          this.existingLoading = false;
          this.cdr.detectChanges();
        }
      );
  }

  reset(): void {
    this.isInitialized = false;
    this.accounts = [];
    this.delTransDetails = [];
    this.RECLASS_DESCRIPTION = '';
    this.modal.R_STD_DESC = '';
    this.modal.R_CMT = '';
    this.TRANS_HEADER_KEY = '';
    this.isMaxLimitReached = false;
    this.viewOnly = false;
    this.tpSearchResults = [];
    this.activeTPSearchIndex = -1;
    this.codeCombination = null;
    this.init();
  }

  save(): void {
    this.isMaxLimitReached = false;
    if (!this.RECLASS_DESCRIPTION || this.RECLASS_DESCRIPTION.trim() === '') {
      this.gtwSnackbarService.openSnackBar('Book Adjustment Description is required', 'gtw-snackbar--yellow');
      return;
    }
    if (this.reclassTotal !== 0) {
      this.gtwSnackbarService.openSnackBar('Book Adjustment must net to 0', 'gtw-snackbar--yellow');
      return;
    }
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', '');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    let reclassMainType: string;
    let reclassSubtype: string;
    switch (this.RECLASS_TYPE) {
      case 'RCY':
        reclassMainType = 'R';
        reclassSubtype = 'M';
        break;
      case 'CYD':
        reclassMainType = 'R';
        reclassSubtype = 'C';
        break;
      case 'PYD':
        reclassMainType = 'R';
        reclassSubtype = 'P';
        break;
      case 'SCY':
        reclassMainType = 'S';
        reclassSubtype = 'M';
        break;
      default:
        reclassMainType = 'R';
        reclassSubtype = 'M';
    }

    const txIssueId = this.globalService?.globalParams?.issue_id_for_tx_save;
    const issueKey = txIssueId === undefined || txIssueId === '' ? (this.globalService?.globalParams?.issue_id ? parseInt(this.globalService.globalParams.issue_id) : null) : txIssueId;

    const returnObj: any = {
      RECLASS_DESCRIPTION: this.RECLASS_DESCRIPTION,
      ISSUE_KEY: issueKey,
      TYPE: this.TRANS_HEADER_KEY !== '' ? 'U' : 'C',
      TRANS_HEADER_KEY: this.TRANS_HEADER_KEY !== '' ? this.TRANS_HEADER_KEY : 0,
      tax_year: this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      RECLASSMAINTYPE: reclassMainType,
      RECLASSSUBTYPE: reclassSubtype,
      ADJ_ORIGIN_SOURCE: this.rowData.SOURCE_SYSTEM,
      SOURCE_SYSTEM_FLAG: this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      SCREEN_KEY: 23,
      TRANS_DETAILS_KEY: {}
    };

    if (this.rowData.SOURCE_SYSTEM === 'DCS') {
      const descMatch = this.reclassDescFullData.find((i: any) => i.RECLASSSTDDESC === this.modal.R_STD_DESC);
      returnObj.reclassStdDesc = descMatch ? descMatch.RECLASS_STD_DESC_KEY : '';
      const cmtMatch = this.commentsDescFullData.find((i: any) => i.RECLASSSTDCOMMENTS === this.modal.R_CMT);
      returnObj.reclassStdComments = cmtMatch ? cmtMatch.RECLASS_STD_COMMENTS_KEY : '';
    }

    const accountArray = [
      ...this.accounts.map(a => ({ ACCOUNT_CODE: a.LOCAL_ACCT, ACCT_KEY: a.LOCAL_ACCT_KEY, TP_KEY: a.TP_KEY, AMOUNT: a.CUR_ADJ_AMT, TRANS_DETAILS_KEY: a.TRANS_DETAILS_KEY, IS_DELETE: a.IS_DELETE })),
      ...this.delTransDetails.map(a => ({ ACCOUNT_CODE: a.LOCAL_ACCT, ACCT_KEY: a.LOCAL_ACCT_KEY, TP_KEY: a.TP_KEY, AMOUNT: a.CUR_ADJ_AMT, TRANS_DETAILS_KEY: a.TRANS_DETAILS_KEY, IS_DELETE: a.IS_DELETE }))
    ];

    const message = returnObj.TYPE === 'U' ? 'Reclass has been successfully updated' : 'Reclass has been successfully created';
    this.service.saveReclass(this.GENERAL_CONFIG.base_url, [returnObj], accountArray, this.globalService, this.filterParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number): number {
    return index;
  }
}

